import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Button,
  Descriptions,
  Form,
  Input,
  Modal,
  Space,
  Typography,
  message,
} from "antd";
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  MobileOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import Utilities from "../../../dependencies/custom/react-utilities";
import domainSettings from "../../../dependencies/custom/settings";
import FinanceService from "../../../dependencies/custom/financeService";

const { Text } = Typography;

const FINAL_SUCCESS = "Valid";
const FINAL_FAILURE = "Suspense";

const SurePayPaymentModal = ({
  open,
  onClose,
  onSuccess,
  paymentCode,
  codeType,
  amount,
  description = "School payment",
  title = "Mobile Money Payment",
  studentName,
  feeLabel,
  autoPoll = true,
}) => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [initiating, setInitiating] = useState(false);
  const [checking, setChecking] = useState(false);
  const [transactionId, setTransactionId] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [gatewayResponse, setGatewayResponse] = useState(null);
  const [pollCount, setPollCount] = useState(0);
  const successHandledRef = useRef(false);

  const amountValue = useMemo(() => {
    const parsed = parseFloat(amount);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  }, [amount]);

  useEffect(() => {
    if (!open) {
      form.resetFields();
      setInitiating(false);
      setChecking(false);
      setTransactionId(null);
      setPaymentStatus(null);
      setGatewayResponse(null);
      setPollCount(0);
      successHandledRef.current = false;
    }
  }, [form, open]);

  useEffect(() => {
    if (
      !open ||
      !autoPoll ||
      !transactionId ||
      paymentStatus === FINAL_SUCCESS ||
      paymentStatus === FINAL_FAILURE
    ) {
      return undefined;
    }

    if (pollCount >= 12) {
      return undefined;
    }

    const timer = setTimeout(() => {
      checkStatus({ silent: true });
      setPollCount((current) => current + 1);
    }, 5000);

    return () => clearTimeout(timer);
    // checkStatus intentionally omitted; it depends on transient loading state.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoPoll, open, paymentStatus, pollCount, transactionId]);

  const validatePhoneNumber = (_, value) => {
    const digits = String(value || "").replace(/\D/g, "");
    if (!digits) {
      return Promise.reject(new Error("Enter a mobile money phone number"));
    }
    if (digits.length === 9 && digits.startsWith("7")) {
      return Promise.resolve();
    }
    return Promise.reject(
      new Error("Enter 9 digits after +256, for example 771234567"),
    );
  };

  const handleInitiate = async () => {
    const values = await form.validateFields();
    setInitiating(true);

    try {
      const payload = {
        paymentCode,
        codeType,
        description,
        phoneNumber: `256${String(values.phoneNumber || "").replace(/\D/g, "")}`,
      };

      if (amountValue !== null) payload.amount = amountValue;

      const response = await Utilities.fetchWithAuth(
        "POST",
        domainSettings.urls.myfinance.initiatePayment,
        payload,
        navigate,
      );

      if (response.ok && response.data?.success) {
        const nextTransactionId =
          response.data.transactionId || response.data.merchantReference;
        setTransactionId(nextTransactionId);
        setPaymentStatus("Pending Validation");
        setGatewayResponse(response.data.gatewayResponse || null);
        message.success(
          "Payment prompt sent. Please approve it on your phone.",
        );
      } else {
        Utilities.showError(
          response,
          response.data?.error || "Failed to initiate payment",
        );
      }
    } catch (error) {
      if (error?.errorFields) return;
      console.error("SurePay initiation error:", error);
      message.error("An error occurred while initiating payment");
    } finally {
      setInitiating(false);
    }
  };

  const checkStatus = async ({ silent = false } = {}) => {
    if (!transactionId) return;

    if (!silent) setChecking(true);
    try {
      const query = new URLSearchParams({ transactionId }).toString();
      const response = await Utilities.fetchWithAuth(
        "GET",
        `${domainSettings.urls.myfinance.queryTransaction}?${query}`,
        null,
        navigate,
      );

      if (!response.ok) {
        if (!silent)
          Utilities.showError(response, "Failed to check payment status");
        return;
      }

      const nextStatus = response.data?.paymentStatus || "Pending Validation";
      setPaymentStatus(nextStatus);
      setGatewayResponse(response.data?.gatewayResponse || null);

      if (nextStatus === FINAL_SUCCESS && !successHandledRef.current) {
        successHandledRef.current = true;
        message.success("Payment confirmed");
        onSuccess?.(response.data);
      } else if (nextStatus === FINAL_FAILURE && !silent) {
        message.warning(
          "Payment was not confirmed. Finance may need to review it.",
        );
      } else if (!silent) {
        message.info("Payment is still processing");
      }
    } catch (error) {
      console.error("SurePay status check error:", error);
      if (!silent)
        message.error("An error occurred while checking payment status");
    } finally {
      if (!silent) setChecking(false);
    }
  };

  const statusAlert = () => {
    if (!transactionId) {
      return (
        <Alert type="info" showIcon message="Enter your mobile money number" />
      );
    }

    if (paymentStatus === FINAL_SUCCESS) {
      return (
        <Alert
          type="success"
          showIcon
          icon={<CheckCircleOutlined />}
          message="Payment confirmed"
        />
      );
    }

    if (paymentStatus === FINAL_FAILURE) {
      return (
        <Alert
          type="warning"
          showIcon
          message="Payment not confirmed"
          description="The transaction was moved to suspense or requires review."
        />
      );
    }

    return (
      <Alert
        type="success"
        showIcon
        icon={<ClockCircleOutlined />}
        message="Payment prompt sent"
        description="Approve the prompt on your phone, then wait here or check the status manually."
      />
    );
  };

  return (
    <Modal
      title={title}
      open={open}
      onCancel={onClose}
      destroyOnClose
      footer={[
        <Button key="close" onClick={onClose}>
          Close
        </Button>,
        transactionId ? (
          <Button
            key="status"
            type="primary"
            icon={<ReloadOutlined />}
            loading={checking}
            onClick={() => checkStatus()}
            disabled={paymentStatus === FINAL_SUCCESS}
          >
            Check Payment Status
          </Button>
        ) : (
          <Button
            key="pay"
            type="primary"
            icon={<MobileOutlined />}
            loading={initiating}
            onClick={handleInitiate}
          >
            Pay Now
          </Button>
        ),
      ]}
    >
      <Space direction="vertical" size="middle" style={{ width: "100%" }}>
        <Descriptions bordered size="small" column={1}>
          {studentName && (
            <Descriptions.Item label="Student">{studentName}</Descriptions.Item>
          )}
          {feeLabel && (
            <Descriptions.Item label="Payment For">
              {feeLabel}
            </Descriptions.Item>
          )}
          <Descriptions.Item label="Amount">
            <Text strong>
              {amountValue !== null
                ? FinanceService.formatCurrency(amountValue)
                : "Outstanding balance"}
            </Text>
          </Descriptions.Item>
          <Descriptions.Item label="Payment Code">
            <Text code>{paymentCode}</Text>
          </Descriptions.Item>
          {transactionId && (
            <Descriptions.Item label="Transaction ID">
              <Text code copyable>
                {transactionId}
              </Text>
            </Descriptions.Item>
          )}
        </Descriptions>

        {statusAlert()}

        {!transactionId && (
          <Form form={form} layout="vertical">
            <Form.Item
              name="phoneNumber"
              label="Mobile Money Number"
              rules={[{ validator: validatePhoneNumber }]}
            >
              <Input
                type="number"
                size="large"
                addonBefore="+256"
                prefix={<MobileOutlined />}
                placeholder="771234567"
              />
            </Form.Item>
          </Form>
        )}

        {gatewayResponse?.message && (
          <Text type="secondary">{gatewayResponse.message}</Text>
        )}
      </Space>
    </Modal>
  );
};

export default SurePayPaymentModal;
